import CloseIcon from '@mui/icons-material/Close'
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined'
import FolderOffOutlined from '@mui/icons-material/FolderOffOutlined'
import NavigateNext from '@mui/icons-material/NavigateNext'
import SearchOutlined from '@mui/icons-material/Search'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import ImageOutlined from '@mui/icons-material/ImageOutlined'
import VideocamOutlined from '@mui/icons-material/VideocamOutlined'
import TableChart from '@mui/icons-material/TableChart'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import {
  Backdrop,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  InputAdornment,
  Link,
  Pagination,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  FileRepositoryModel,
  FileRepositoryListParams,
} from 'src/models/file-repository-model'
import { getPermissions, getRole } from 'src/service/authService'
import { fetchFileRepositoryEntries, uploadFileRepository } from 'src/service/fileRepositoryService'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import { formatFileSize } from '../utils/fileHelpers'
import { UploadFileModal } from './UploadFileModal'

// ── Types ────────────────────────────────────────────────────────────
export type MimeFilter = 'image' | 'video' | 'all'

interface BreadcrumbItem {
  id: string | null
  name: string
}

interface FileRepositorySelectorModalProps {
  open: boolean
  onClose: () => void
  onSelect: (fileUrl: string, fileName: string) => void
  /** Filter files shown: 'image' for .png/.jpg, 'video' for .mp4, 'all' for everything */
  mimeFilter?: MimeFilter
  title?: string
}

// ── Helpers ──────────────────────────────────────────────────────────
const getFileIcon = (entry: FileRepositoryModel) => {
  if (entry.type === 'DIRECTORY') {
    return <FolderOutlined sx={{ fontSize: 40, color: '#FF5E1E' }} />
  }
  if (entry.mimeType?.startsWith('image/')) {
    return <ImageOutlined sx={{ fontSize: 40, color: 'rgba(0,0,0,0.4)' }} />
  }
  if (entry.mimeType === 'video/mp4') {
    return <VideocamOutlined sx={{ fontSize: 40, color: 'rgba(0,0,0,0.4)' }} />
  }
  if (entry.mimeType === 'text/csv') {
    return <TableChart sx={{ fontSize: 40, color: 'rgba(0,0,0,0.4)' }} />
  }
  return <ImageOutlined sx={{ fontSize: 40, color: 'rgba(0,0,0,0.4)' }} />
}

const matchesMimeFilter = (entry: FileRepositoryModel, filter: MimeFilter): boolean => {
  // Always show directories so user can navigate
  if (entry.type === 'DIRECTORY') return true
  if (filter === 'all') return true
  if (filter === 'image') return entry.mimeType?.startsWith('image/') ?? false
  if (filter === 'video') return entry.mimeType === 'video/mp4'
  return true
}

// ── Component ────────────────────────────────────────────────────────
export function FileRepositorySelectorModal({
  open,
  onClose,
  onSelect,
  mimeFilter = 'all',
  title,
}: FileRepositorySelectorModalProps) {
  const { t } = useTranslation()

  // State
  const [entries, setEntries] = useState<FileRepositoryModel[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [currentParentId, setCurrentParentId] = useState<string | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: null, name: 'fileRepository.breadcrumb.root' },
  ])
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [selectedEntry, setSelectedEntry] = useState<FileRepositoryModel | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const limit = 20

  // TECNOIT handling
  const isTecnoit = getRole() === 'TECNOIT'
  const { isGlobalAdminSelected, selectedCompanyData } = useGlobalSelectedCompany()

  const tecnoitCompanyId = useMemo(() => {
    if (!isTecnoit) return undefined
    if (!isGlobalAdminSelected() && selectedCompanyData) {
      const company = selectedCompanyData as any
      return (company.id || company.companyId) as string
    }
    return undefined
  }, [isTecnoit, selectedCompanyData])

  // Permissions
  const permissions = useMemo(() => JSON.parse(getPermissions()), [])
  const canCreate =
    permissions?.['file-repository']?.includes('file-repository:create') ?? false

  // ── Load entries ────────────────────────────────────────────────
  const loadEntries = useCallback(async () => {
    setLoading(true)
    const params: FileRepositoryListParams = {
      parentId: currentParentId,
      page,
      limit,
      sortBy: 'name',
      sortOrder: 'asc',
    }
    if (search) params.search = search
    if (isTecnoit && tecnoitCompanyId) params.companyId = tecnoitCompanyId

    const result = await fetchFileRepositoryEntries(params)
    if (result) {
      setEntries(result.data)
      setTotal(result.total)
    }
    setLoading(false)
  }, [currentParentId, page, search, tecnoitCompanyId])

  useEffect(() => {
    if (open) loadEntries()
  }, [open, loadEntries])

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCurrentParentId(null)
      setBreadcrumbs([{ id: null, name: 'fileRepository.breadcrumb.root' }])
      setPage(1)
      setSearch('')
      setSelectedEntry(null)
    }
  }, [open])

  // ── Navigation ─────────────────────────────────────────────────
  const navigateToDirectory = (dir: FileRepositoryModel) => {
    setCurrentParentId(dir.id)
    setBreadcrumbs((prev) => [...prev, { id: dir.id, name: dir.name }])
    setPage(1)
    setSearch('')
    setSelectedEntry(null)
  }

  const navigateToBreadcrumb = (index: number) => {
    const target = breadcrumbs[index]
    setCurrentParentId(target.id)
    setBreadcrumbs(breadcrumbs.slice(0, index + 1))
    setPage(1)
    setSearch('')
    setSelectedEntry(null)
  }

  // ── Upload handler ─────────────────────────────────────────────
  const handleUpload = async (
    file: File,
    description?: string,
    companyId?: string
  ) => {
    const res = await uploadFileRepository(
      file,
      currentParentId,
      description,
      companyId || tecnoitCompanyId
    )
    if (res) {
      loadEntries()
    }
    return res
  }

  // ── Selection ──────────────────────────────────────────────────
  const handleCardClick = (entry: FileRepositoryModel) => {
    if (entry.type === 'DIRECTORY') {
      navigateToDirectory(entry)
    } else {
      setSelectedEntry(entry)
    }
  }

  const handleConfirmSelection = () => {
    if (selectedEntry?.url) {
      onSelect(selectedEntry.url, selectedEntry.name)
      onClose()
    }
  }

  // ── Filtered entries ───────────────────────────────────────────
  const filteredEntries = entries.filter((e) => matchesMimeFilter(e, mimeFilter))
  const totalPages = Math.ceil(total / limit)

  const defaultTitle = mimeFilter === 'image'
    ? t('fileRepository.selector.title.image', 'Selecionar Imagem')
    : mimeFilter === 'video'
      ? t('fileRepository.selector.title.video', 'Selecionar Vídeo')
      : t('fileRepository.selector.title.all', 'Selecionar Arquivo')

  if (!open) return null

  return (
    <>
      {/* Overlay backdrop */}
      <Backdrop
        open={open}
        onClick={onClose}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      />

      {/* Side panel */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: { xs: '100%', sm: '520px', md: '600px' },
          height: '100vh',
          backgroundColor: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        }}
      >
        {/* ── Header ───────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {title || defaultTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── Toolbar: Search + Upload ─────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 1.5,
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <TextField
            size="small"
            placeholder={t('fileRepository.toolbar.search', 'Buscar arquivos...')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {canCreate && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadOutlined />}
              onClick={() => setShowUploadModal(true)}
              sx={{
                borderColor: '#FF5E1E',
                color: '#FF5E1E',
                whiteSpace: 'nowrap',
                ':hover': {
                  borderColor: '#FF5E1E',
                  backgroundColor: 'rgba(255, 94, 30, 0.08)',
                },
              }}
            >
              {t('fileRepository.selector.newFile', 'Novo Arquivo')}
            </Button>
          )}
        </Box>

        {/* ── Breadcrumbs ──────────────────────────────────────── */}
        <Box sx={{ px: 3, pt: 1.5 }}>
          <Breadcrumbs
            separator={<NavigateNext fontSize="small" />}
            sx={{ mb: 1 }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1
              const label = crumb.id === null ? t(crumb.name) : crumb.name
              return isLast ? (
                <Typography key={index} color="text.primary" fontWeight={500} variant="body2">
                  {label}
                </Typography>
              ) : (
                <Link
                  key={index}
                  component="button"
                  underline="hover"
                  color="inherit"
                  variant="body2"
                  onClick={() => navigateToBreadcrumb(index)}
                  sx={{ cursor: 'pointer', ':hover': { color: '#FF5E1E' } }}
                >
                  {label}
                </Link>
              )
            })}
          </Breadcrumbs>
        </Box>

        {/* ── File grid ────────────────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 3,
            py: 1,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 1.5,
              }}
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  width="100%"
                  height={130}
                  sx={{ borderRadius: '8px' }}
                />
              ))}
            </Box>
          ) : filteredEntries.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: 'rgba(0,0,0,0.5)' }}>
              <FolderOffOutlined sx={{ fontSize: 56, mb: 1.5, color: 'rgba(0,0,0,0.25)' }} />
              <Typography variant="body1">
                {t('fileRepository.selector.empty', 'Nenhum arquivo encontrado')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'fileRepository.selector.emptyHint',
                  'Navegue pelas pastas ou faça upload de um novo arquivo.'
                )}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 1.5,
              }}
            >
              {filteredEntries.map((entry) => {
                const isFile = entry.type === 'FILE'
                const isSelected = selectedEntry?.id === entry.id
                const isImage = entry.mimeType?.startsWith('image/')

                return (
                  <Card
                    key={entry.id}
                    variant="outlined"
                    sx={{
                      borderRadius: '8px',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                      borderColor: isSelected ? '#FF5E1E' : undefined,
                      boxShadow: isSelected
                        ? '0 0 0 2px rgba(255, 94, 30, 0.3)'
                        : undefined,
                      ':hover': {
                        borderColor: '#FF5E1E',
                        boxShadow: '0 2px 8px rgba(255, 94, 30, 0.15)',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleCardClick(entry)}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <Box
                        sx={{
                          height: 90,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0,0,0,0.02)',
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                      >
                        {isFile && isImage && entry.url ? (
                          <img
                            src={entry.url}
                            alt={entry.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          getFileIcon(entry)
                        )}

                        {/* Selected indicator */}
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: '#FF5E1E',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CheckCircleOutline
                              sx={{ fontSize: 18, color: '#fff' }}
                            />
                          </Box>
                        )}
                      </Box>
                      <CardContent sx={{ p: '6px 10px !important', flexGrow: 1 }}>
                      <Typography
                        variant="caption"
                        fontWeight={500}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                        }}
                        title={entry.name}
                      >
                        {entry.name}
                      </Typography>
                      {isFile && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          {formatFileSize(entry.sizeInBytes)}
                        </Typography>
                      )}
                    </CardContent>
                    </CardActionArea>
                  </Card>
                )
              })}
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
              <Pagination
                count={totalPages}
                page={page}
                size="small"
                shape="rounded"
                onChange={(_, p) => setPage(p)}
              />
            </Box>
          )}
        </Box>

        {/* ── Footer: selection info + confirm ─────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderTop: '1px solid rgba(0,0,0,0.12)',
            backgroundColor: 'rgba(0,0,0,0.02)',
          }}
        >
          <Box sx={{ overflow: 'hidden', mr: 2 }}>
            {selectedEntry ? (
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <strong>{t('fileRepository.selector.selected', 'Selecionado')}:</strong>{' '}
                {selectedEntry.name}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(
                  'fileRepository.selector.selectHint',
                  'Selecione um arquivo para continuar'
                )}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button onClick={onClose} size="small">
              {t('general.button.cancel', 'Cancelar')}
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!selectedEntry}
              onClick={handleConfirmSelection}
              sx={{
                backgroundColor: '#FF5E1E',
                ':hover': { backgroundColor: '#e04e10' },
              }}
            >
              {t('fileRepository.selector.confirm', 'Confirmar')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Upload modal (reuse existing) */}
      <UploadFileModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
      />
    </>
  )
}
